import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// ── Types ──

export interface BrainChunk {
  id: number;
  category: string;
  subcategory: string | null;
  title: string;
  content: string;
  sourceType: string | null;
  vertical: string | null;
  importance: number | null;
  tokenCount: number | null;
  metadata: Record<string, unknown> | null;
  isArchived: boolean | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface BrainConnection {
  id: number;
  relationship: string;
  strength: number;
  chunk: Pick<BrainChunk, "id" | "title" | "category"> | null;
}

export interface GraphNode {
  id: number;
  category: string;
  title: string;
  importance: number;
  createdAt: string | null;
  subcategory: string | null;
  sourceType: string | null;
  vertical: string | null;
}

export interface GraphEdge {
  source: number;
  target: number;
  relationshipType: string;
  strength: number;
}

export interface BrainStats {
  totalChunks: number;
  totalConnections: number;
  chunksByCategory: Record<string, number>;
  newThisWeek: number;
  activeRubric: { id: number; name: string; versionNumber: number } | null;
  accuracyScore: number | null;
}

export interface BrainSettings {
  id: number;
  isEnabled: boolean;
  maxChunksTotal: number;
  chunkRetentionDays: number;
  autoIngestEnabled: boolean;
  simulationEnabled: boolean;
}

export interface RubricRule {
  id: string;
  rule_text: string;
  weight: number;
  source: string;
  category: string;
  enabled: boolean;
}

export interface RubricVersion {
  id: number;
  versionNumber: number;
  name: string;
  description: string | null;
  isActive: boolean | null;
  rules: RubricRule[] | null;
  accuracyScore: number | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CalibrationAd {
  id: number;
  adId: string | null;
  platform: string;
  vertical: string | null;
  format: string;
  headline: string | null;
  body: string | null;
  imageUrl: string | null;
  videoUrl: string | null;
  thumbnailUrl: string | null;
  isWinner: boolean | null;
  winRate: number | null;
  impressions: number | null;
  clicks: number | null;
  conversions: number | null;
  roas: number | null;
  ctaType: string | null;
  notes: string | null;
  createdAt: string | null;
}

export interface CalibrationStats {
  total: number;
  winners: number;
  byPlatform: Record<string, number>;
  byVertical: Record<string, number>;
  byFormat: Record<string, number>;
  avgRoas: number | null;
}

// ── Brain ──

export function useBrainStats() {
  return useQuery<BrainStats>({
    queryKey: ["/api/admin/brain/stats"],
    queryFn: async () => {
      const res = await fetch("/api/admin/brain/stats");
      if (!res.ok) throw new Error("Failed to fetch brain stats");
      return res.json();
    },
    staleTime: 30_000,
  });
}

export function useBrainGraph(categories?: string[], search?: string) {
  const params = new URLSearchParams();
  if (categories?.length) params.set("categories", categories.join(","));
  if (search) params.set("search", search);
  const qs = params.toString();

  return useQuery<{ nodes: GraphNode[]; edges: GraphEdge[] }>({
    queryKey: ["/api/admin/brain/graph", qs],
    queryFn: async () => {
      const res = await fetch(`/api/admin/brain/graph${qs ? `?${qs}` : ""}`);
      if (!res.ok) throw new Error("Failed to fetch graph");
      return res.json();
    },
    staleTime: 30_000,
  });
}

export function useBrainChunks(page = 1, limit = 100, category?: string) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (category) params.set("category", category);

  return useQuery<{ chunks: BrainChunk[]; total: number; page: number; limit: number }>({
    queryKey: ["/api/admin/brain/chunks", page, limit, category],
    queryFn: async () => {
      const res = await fetch(`/api/admin/brain/chunks?${params}`);
      if (!res.ok) throw new Error("Failed to fetch chunks");
      return res.json();
    },
    staleTime: 30_000,
  });
}

export function useBrainChunkDetail(chunkId: number | null) {
  return useQuery<{ chunk: BrainChunk; connections: BrainConnection[] }>({
    queryKey: ["/api/admin/brain/chunks", chunkId],
    queryFn: async () => {
      const res = await fetch(`/api/admin/brain/chunks/${chunkId}`);
      if (!res.ok) throw new Error("Failed to fetch chunk detail");
      return res.json();
    },
    enabled: !!chunkId,
    staleTime: 15_000,
  });
}

export function useCreateBrainChunk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      title: string;
      content: string;
      category: string;
      subcategory?: string;
      vertical?: string;
      importance?: number;
    }) => {
      const res = await fetch("/api/admin/brain/chunks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create chunk");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/brain/graph"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/brain/stats"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/brain/chunks"] });
    },
  });
}

export function useUpdateBrainChunk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: number; title?: string; content?: string; importance?: number; vertical?: string }) => {
      const res = await fetch(`/api/admin/brain/chunks/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update chunk");
      return res.json();
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/admin/brain/chunks", vars.id] });
      qc.invalidateQueries({ queryKey: ["/api/admin/brain/graph"] });
    },
  });
}

export function useDeleteBrainChunk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/admin/brain/chunks/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete chunk");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/brain/graph"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/brain/stats"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/brain/chunks"] });
    },
  });
}

export function useBrainSettings() {
  return useQuery<BrainSettings>({
    queryKey: ["/api/admin/brain/settings"],
    queryFn: async () => {
      const res = await fetch("/api/admin/brain/settings");
      if (!res.ok) throw new Error("Failed to fetch settings");
      return res.json();
    },
    staleTime: 60_000,
  });
}

export function useUpdateBrainSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<BrainSettings>) => {
      const res = await fetch("/api/admin/brain/settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update settings");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/brain/settings"] });
    },
  });
}

// ── Rubrics ──

export function useRubricVersions() {
  return useQuery<RubricVersion[]>({
    queryKey: ["/api/admin/rubrics"],
    queryFn: async () => {
      const res = await fetch("/api/admin/rubrics");
      if (!res.ok) throw new Error("Failed to fetch rubrics");
      return res.json();
    },
    staleTime: 30_000,
  });
}

export function useActiveRubric() {
  return useQuery<RubricVersion | null>({
    queryKey: ["/api/admin/rubrics/active"],
    queryFn: async () => {
      const res = await fetch("/api/admin/rubrics/active");
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to fetch active rubric");
      return res.json();
    },
    staleTime: 30_000,
  });
}

export function useRubricVersion(id: number | null) {
  return useQuery<RubricVersion>({
    queryKey: ["/api/admin/rubrics", id],
    queryFn: async () => {
      const res = await fetch(`/api/admin/rubrics/${id}`);
      if (!res.ok) throw new Error("Failed to fetch rubric");
      return res.json();
    },
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useCreateRubricVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      const res = await fetch("/api/admin/rubrics", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create rubric");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/rubrics"] });
    },
  });
}

export function useActivateRubric() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/admin/rubrics/${id}/activate`, { method: "POST" });
      if (!res.ok) throw new Error("Failed to activate rubric");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/rubrics"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/rubrics/active"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/brain/stats"] });
    },
  });
}

export function useAddRubricRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ versionId, rule }: {
      versionId: number;
      rule: { rule_text: string; weight: number; source?: string; category?: string; enabled?: boolean };
    }) => {
      const res = await fetch(`/api/admin/rubrics/${versionId}/rules`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(rule),
      });
      if (!res.ok) throw new Error("Failed to add rule");
      return res.json();
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/admin/rubrics", vars.versionId] });
      qc.invalidateQueries({ queryKey: ["/api/admin/rubrics"] });
    },
  });
}

export function useToggleRubricRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ versionId, ruleId, enabled }: { versionId: number; ruleId: string; enabled: boolean }) => {
      const res = await fetch(`/api/admin/rubrics/${versionId}/rules/${ruleId}/toggle`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) throw new Error("Failed to toggle rule");
      return res.json();
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/admin/rubrics", vars.versionId] });
      qc.invalidateQueries({ queryKey: ["/api/admin/rubrics"] });
    },
  });
}

export function useDeleteRubricRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ versionId, ruleId }: { versionId: number; ruleId: string }) => {
      const res = await fetch(`/api/admin/rubrics/${versionId}/rules/${ruleId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete rule");
      return res.json();
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/admin/rubrics", vars.versionId] });
      qc.invalidateQueries({ queryKey: ["/api/admin/rubrics"] });
    },
  });
}

export function useUpdateRubricVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: number; name?: string; description?: string }) => {
      const res = await fetch(`/api/admin/rubrics/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update rubric version");
      return res.json();
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/admin/rubrics", vars.id] });
      qc.invalidateQueries({ queryKey: ["/api/admin/rubrics"] });
    },
  });
}

// ── Calibration Ads ──

export function useCalibrationAds(filters?: {
  isWinner?: boolean;
  vertical?: string;
  format?: string;
  platform?: string;
  page?: number;
  limit?: number;
}) {
  const params = new URLSearchParams();
  if (filters?.isWinner !== undefined) params.set("is_winner", String(filters.isWinner));
  if (filters?.vertical) params.set("vertical", filters.vertical);
  if (filters?.format) params.set("format", filters.format);
  if (filters?.platform) params.set("platform", filters.platform);
  if (filters?.page) params.set("page", String(filters.page));
  if (filters?.limit) params.set("limit", String(filters.limit));

  return useQuery<{ ads: CalibrationAd[]; total: number; page: number; limit: number }>({
    queryKey: ["/api/admin/calibration-ads", filters],
    queryFn: async () => {
      const res = await fetch(`/api/admin/calibration-ads?${params}`);
      if (!res.ok) throw new Error("Failed to fetch calibration ads");
      return res.json();
    },
    staleTime: 30_000,
  });
}

export function useCalibrationStats() {
  return useQuery<CalibrationStats>({
    queryKey: ["/api/admin/calibration-ads/stats"],
    queryFn: async () => {
      const res = await fetch("/api/admin/calibration-ads/stats");
      if (!res.ok) throw new Error("Failed to fetch calibration stats");
      return res.json();
    },
    staleTime: 30_000,
  });
}

export function useUpdateCalibrationAd() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: number } & Partial<CalibrationAd>) => {
      const res = await fetch(`/api/admin/calibration-ads/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update calibration ad");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/calibration-ads"] });
    },
  });
}

// ── Heartbeat Diagnostics ──

export function useHeartbeatDiagnostics(enabled: boolean) {
  return useQuery<Record<string, unknown>>({
    queryKey: ["/api/admin/heartbeat-diagnostics"],
    queryFn: async () => {
      const res = await fetch("/api/admin/heartbeat-diagnostics");
      if (!res.ok) throw new Error("Failed to run heartbeat diagnostics");
      return res.json() as Promise<Record<string, unknown>>;
    },
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

// ── Platform Docs ──

export interface PlatformDoc {
  filename: string;
  exists: boolean;
  sizeBytes: number;
  lastModified: string | null;
}

export function usePlatformDocs() {
  return useQuery<PlatformDoc[]>({
    queryKey: ["/api/admin/platform-docs"],
    queryFn: async () => {
      const res = await fetch("/api/admin/platform-docs");
      if (!res.ok) throw new Error("Failed to fetch platform docs");
      return res.json();
    },
    staleTime: 60_000,
  });
}

export function usePlatformDocContent(filename: string | null) {
  return useQuery<string>({
    queryKey: ["/api/admin/platform-docs", filename],
    queryFn: async () => {
      const res = await fetch(`/api/admin/platform-docs/${filename}`);
      if (!res.ok) throw new Error("Failed to fetch document");
      return res.text();
    },
    enabled: !!filename,
    staleTime: 60_000,
  });
}
